import { useState } from 'react';
import {
  ExternalLink,
  MessageSquareReply,
  PenSquare,
  TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  IntegrationStatus,
  type IntegrationHealth,
} from '@/components/organisms/settings-integrations';
import { useDisconnectPlatform } from '@/hooks/review-platforms/useDisconnectPlatform';
import {
  useSetPlatformAutoPost,
  useSetReplyMode,
} from '@/hooks/review-platforms/useAutoPostSettings';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  PLATFORM_CAPABILITIES,
  type PlatformConfigRecord,
  type PlatformConnectionStatus,
  type ReviewPlatform,
} from '@/types/review-platform';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { OAuthConnectFlow } from './OAuthConnectFlow';
import { ReviewPlatformLogo } from './ReviewPlatformLogo';
import { UrlPasteForm } from './UrlPasteForm';

interface PlatformConnectCardProps {
  platform: ReviewPlatform;
  config: PlatformConfigRecord | null;
  locationId: string | null;
  canEdit?: boolean;
}

/**
 * One card per platform we offer to connect.
 *
 * Built on the same four-band stack as `IntegrationCard` on Connected Apps —
 * brand, blurb, facts, actions — and on that page's status vocabulary, because
 * a contractor reaches both screens from the same settings nav and they should
 * not look like two products. In particular the status is a coloured dot plus
 * near-neutral text rather than a filled pill: a column of filled pills beside
 * four brand marks turns the list into a swatch board, which is what the
 * previous badge-and-two-capability-chips row had become.
 *
 * Capability is a fact, not a badge. "Read: Yes / Reply: No" as chips read as
 * status — as though something were wrong with Yelp — when it is a fixed
 * property of the platform's API that never changes.
 */
type ReplyMode = 'off' | 'manual' | 'automatic';

/** The two booleans, read as the one decision they describe. */
function replyMode(config: { writeEnabled?: boolean; autoPostEnabled?: boolean }): ReplyMode {
  if (!config.writeEnabled) return 'off';
  return config.autoPostEnabled ? 'automatic' : 'manual';
}

/**
 * What each mode means, in the second person. The select says what will
 * happen; this says what that costs you, which is the part people are
 * actually choosing between.
 */
const REPLY_MODE_HELP: Record<ReplyMode, (label: string) => string> = {
  off: (label) => `Replies have to be written on ${label} itself.`,
  manual: () => 'Drafts wait here until someone sends them.',
  automatic: () =>
    'AI drafts go out on their own. You can read and stop them under Auto-post preview.',
};

export function PlatformConnectCard({
  platform,
  config,
  locationId,
  canEdit = true,
}: PlatformConnectCardProps) {
  const cap = PLATFORM_CAPABILITIES[platform];
  const [connectOpen, setConnectOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [replySettingsOpen, setReplySettingsOpen] = useState(false);
  const [reconnectOpen, setReconnectOpen] = useState(false);
  const disconnect = useDisconnectPlatform(locationId);
  const setReplyMode = useSetReplyMode(locationId);
  // The delay is a single field on an already-automatic platform, so it has
  // none of the pairing risk the mode had and keeps its own narrow endpoint.
  const setAutoPost = useSetPlatformAutoPost(locationId);
  const companyTz = useCompanyTimezone();

  const status = config?.status ?? 'DISCONNECTED';
  const isOauth = cap.authType === 'OAUTH';
  const isConnected = !!config && status !== 'DISCONNECTED';
  const health = STATUS_HEALTH[status];

  function handleDisconnect() {
    if (!config) return;
    disconnect.mutate(
      { platformConfigId: config.id },
      { onSuccess: () => setDisconnectOpen(false) },
    );
  }

  const lastSynced = config?.lastSyncedAt
    ? formatDateTimeInTimeZone(config.lastSyncedAt, companyTz, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'Not yet';

  return (
    <article
      data-testid={`platform-card-${platform}`}
      data-health={health}
      className="grid gap-x-6 gap-y-4 px-5 py-5 transition-colors hover:bg-muted/[0.16] sm:grid-cols-[13rem_minmax(0,1fr)] sm:px-6"
    >
      <div className="flex min-w-0 items-center gap-3">
        <ReviewPlatformLogo platform={platform} />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm leading-5 font-semibold">{cap.label}</h3>
          <div className="mt-1">
            <IntegrationStatus health={health} label={STATUS_LABEL[status]} />
          </div>
        </div>
      </div>

      <div className="min-w-0">
        <p className="text-[13px] font-medium leading-5 text-foreground">
          {capabilityText(cap.read, cap.write)}
        </p>
        <p className="mt-0.5 text-[13px] leading-5 text-muted-foreground">
          {isConnected ? `Last synced ${lastSynced}` : platformSummary(cap)}
        </p>

        {config?.lastSyncError ? (
          <div className="mt-2 flex gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-[13px] text-destructive">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <p className="min-w-0 flex-1 leading-5">
              {syncErrorMessage(cap.label, config.lastSyncError)}
            </p>
          </div>
        ) : null}

      </div>

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-4 sm:col-start-2 sm:pt-3.5">
          {!isConnected ? (
            <>
          {isOauth ? (
            <OAuthConnectFlow
              platform={platform as 'GOOGLE' | 'FACEBOOK'}
              locationId={locationId}
              label="Connect"
            />
          ) : (
            <Button size="sm" onClick={() => setConnectOpen(true)}>
              <PenSquare className="size-3.5" />
              Connect
            </Button>
          )}
            </>
          ) : (
            <>
          {status === 'NEEDS_REAUTH' && isOauth ? (
            <Button size="sm" onClick={() => setReconnectOpen(true)}>
              Reconnect
            </Button>
          ) : null}
          {cap.write && config ? (
            <Button
              size="sm"
              variant={status === 'NEEDS_REAUTH' ? 'outline' : 'default'}
              onClick={() => setReplySettingsOpen(true)}
            >
              <MessageSquareReply className="size-3.5" />
              Reply settings
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            className="px-2 text-muted-foreground hover:text-destructive"
            onClick={() => setDisconnectOpen(true)}
          >
            Disconnect
          </Button>
          {config?.profileUrl ? (
            <a
              href={config.profileUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={`Open ${cap.label} listing`}
            >
              <ExternalLink className="size-3.5" />
            </a>
          ) : null}
            </>
          )}
        </div>
      ) : null}

      {/*
        Reply settings, in a dialog rather than on the card.

        The card is read at a glance across a row of four; this is opened
        deliberately by one person changing one platform. Giving it a dialog
        means the choice can be spelled out — each mode with its consequence —
        instead of compressed into a 287px column where the label did not fit.
      */}
      {config && cap.write ? (
        <Dialog open={replySettingsOpen} onOpenChange={setReplySettingsOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Replies to {cap.label}</DialogTitle>
              <DialogDescription>Who sends a reply once a draft exists.</DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {/*
                One request, not two. This applied the choice as separate
                PATCHes for `writeEnabled` and `autoPostEnabled`, and either
                could fail alone. The worst leftover armed auto-post on a
                platform the card then rendered as "off" — because the mode is
                derived from `writeEnabled` — so turning replies back on later
                would start posting automatically with nobody having chosen it
                a second time. The server now sets the pair in one write.
              */}
              <Select
                value={replyMode(config)}
                disabled={setReplyMode.isPending}
                onValueChange={(next) => {
                  setReplyMode.mutate({
                    platformConfigId: config.id,
                    mode: next as ReplyMode,
                  });
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Don&apos;t post from Notifi</SelectItem>
                  <SelectItem value="manual">I send each one</SelectItem>
                  <SelectItem value="automatic">Send AI replies for me</SelectItem>
                </SelectContent>
              </Select>

              <p className="text-[13px] leading-5 text-muted-foreground">
                {REPLY_MODE_HELP[replyMode(config)](cap.label)}
              </p>

              {config.autoPostEnabled ? (
                <label className="flex items-center gap-2 border-t border-border/60 pt-3 text-[13px] text-muted-foreground">
                  Wait
                  <Input
                    type="number"
                    min={1}
                    max={10080}
                    aria-label={`Delay before posting to ${cap.label}, in minutes`}
                    className="h-8 w-20 text-right tabular-nums"
                    defaultValue={config.autoPostDelayMinutes ?? 240}
                    disabled={setAutoPost.isPending}
                    // Committed on blur, not per keystroke: each change is a
                    // PATCH, so typing "120" would fire three and briefly
                    // persist a one-minute window nobody could use.
                    onBlur={(event) => {
                      const next = Number(event.currentTarget.value);
                      if (
                        !Number.isFinite(next) ||
                        next < 1 ||
                        next === config.autoPostDelayMinutes
                      ) {
                        event.currentTarget.value = String(config.autoPostDelayMinutes ?? 240);
                        return;
                      }
                      setAutoPost.mutate({
                        platformConfigId: config.id,
                        value: true,
                        delayMinutes: next,
                      });
                    }}
                  />
                  minutes before a draft goes out
                </label>
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      {/* Reconnect, opened from the menu rather than a button on the card. */}
      {isOauth && reconnectOpen ? (
        <Dialog open={reconnectOpen} onOpenChange={setReconnectOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reconnect {cap.label}</DialogTitle>
              <DialogDescription>
                Authorise Notifi again to resume syncing reviews.
              </DialogDescription>
            </DialogHeader>
            <OAuthConnectFlow
              platform={platform as 'GOOGLE' | 'FACEBOOK'}
              locationId={locationId}
              label="Reconnect"
            />
          </DialogContent>
        </Dialog>
      ) : null}

      {/* URL-paste connect dialog */}
      {!isOauth ? (
        <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Connect {cap.label}</DialogTitle>
              <DialogDescription>Paste your public {cap.label} listing URL.</DialogDescription>
            </DialogHeader>
            <UrlPasteForm
              platform={platform}
              locationId={locationId}
              onSuccess={() => setConnectOpen(false)}
            />
          </DialogContent>
        </Dialog>
      ) : null}

      {/* Disconnect confirmation */}
      <Dialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect {cap.label}?</DialogTitle>
            <DialogDescription>
              Notifi will stop syncing reviews from {cap.label} and{' '}
              {isOauth
                ? 'revoke the OAuth token on the provider side'
                : 'clear the saved profile URL'}
              . You can reconnect at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={disconnect.isPending}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </article>
  );
}

function platformSummary(cap: (typeof PLATFORM_CAPABILITIES)[ReviewPlatform]) {
  if (cap.read && cap.write) return 'Sync incoming reviews and reply without leaving Notifi.';
  if (cap.read) return 'Keep this public profile available for customers and monitor recent reviews.';
  return 'Send customers to this public profile after a positive rating.';
}

/**
 * Provider errors are useful to support staff but not to the person trying to
 * get reviews flowing. Keep the original response out of the UI and explain
 * the one action the customer can take instead.
 *
 * `lastSyncError` carries two different kinds of message in one column: raw
 * provider text, and copy operations wrote FOR this reader
 * (`NOT_CONFIGURED_MESSAGE`). Rewriting both flattened the second kind and
 * contradicted it — a Yelp config with missing server-side credentials says
 * "nothing to do here", and this told the operator to reconnect and try again,
 * which cannot work and is not theirs to do.
 *
 * Matching on the phrasing is admittedly brittle. The durable fix is for the
 * config row to carry `NotConfiguredReason` as a field so the UI can branch on
 * a value instead of on prose; today that reason exists only on the thrown
 * error and is never persisted.
 */
function syncErrorMessage(platformLabel: string, error: string) {
  const normalized = error.toLowerCase();

  // Ours, already addressed to this reader, and describing something they
  // cannot fix. Passing it through unchanged is the whole point of it.
  if (
    normalized.includes('nothing to do here') ||
    normalized.includes("isn't available yet")
  ) {
    return error;
  }

  if (
    normalized.includes('oauth') ||
    normalized.includes('access token') ||
    normalized.includes('not connected')
  ) {
    return `Reconnect ${platformLabel} to resume syncing new reviews.`;
  }

  return `We couldn't sync ${platformLabel} right now. Reconnect it, or try again in a few minutes.`;
}

const STATUS_HEALTH: Record<PlatformConnectionStatus, IntegrationHealth> = {
  CONNECTED: 'connected',
  NEEDS_REAUTH: 'attention',
  ERROR: 'error',
  DISCONNECTED: 'disconnected',
};

const STATUS_LABEL: Record<PlatformConnectionStatus, string> = {
  CONNECTED: 'Connected',
  NEEDS_REAUTH: 'Needs re-auth',
  ERROR: 'Sync error',
  DISCONNECTED: 'Not connected',
};

/**
 * Capability as a sentence rather than two pass/fail chips. What a platform's
 * API allows is fixed — Yelp will not gain a reply endpoint because we waited —
 * so presenting it as state invites the reader to try to fix it.
 */
function capabilityText(read: boolean, write: boolean): string {
  if (read && write) return 'Read and reply';
  if (read) return 'Read only';
  return 'Deep link only';
}
